import type { FC, ReactNode } from 'react'

import { useEffect, useMemo, useRef, useState } from 'react'
import { Play, X } from 'lucide-react'
import { getVideoSource } from '@/lib/video'

interface VideoDialogProps {
    url: string
    label: string
    cms?: string
}

// Pink pill from the brand that opens the video presentation in a modal.
export const VideoDialog: FC<VideoDialogProps> = ({ url, label, cms }): ReactNode => {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const [open, setOpen] = useState(false)
    const source = useMemo(() => getVideoSource(url), [url])

    useEffect(() => {
        const dialog = dialogRef.current

        if (!dialog) return
        if (open && !dialog.open) dialog.showModal()
        if (!open && dialog.open) dialog.close()
    }, [open])

    useEffect(() => {
        const dialog = dialogRef.current

        if (!dialog) return

        const handleClose = () => setOpen(false)

        dialog.addEventListener('close', handleClose)

        return () => dialog.removeEventListener('close', handleClose)
    }, [])

    if (!source) return null

    return (
        <>
            <button
                type='button'
                onClick={() => setOpen(true)}
                className='group inline-flex items-center gap-3 rounded-full bg-accent py-2 pl-2 pr-5 font-mono text-xs font-bold uppercase tracking-[0.14em] text-accent-foreground shadow-[0_10px_30px_-12px_rgba(255,166,236,0.9)] transition-transform duration-300 hover:-translate-y-0.5'
            >
                <span className='flex size-8 items-center justify-center rounded-full bg-foreground text-background transition-transform duration-300 group-hover:scale-110'>
                    <Play aria-hidden='true' className='size-3.5 translate-x-px fill-current' />
                </span>

                <span data-mdlfy-cms={cms}>{label}</span>
            </button>

            <dialog
                ref={dialogRef}
                aria-label={label}
                onClick={event => {
                    if (event.target === event.currentTarget) setOpen(false)
                }}
                className='m-auto w-[min(92vw,960px)] overflow-visible bg-transparent p-0 backdrop:bg-black/80 backdrop:backdrop-blur-sm'
            >
                {open && (
                    <div className='relative pt-14'>
                        <button
                            type='button'
                            onClick={() => setOpen(false)}
                            className='absolute right-0 top-0 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 font-mono text-xs font-bold uppercase tracking-[0.14em] text-foreground'
                        >
                            Close
                            <X aria-hidden='true' className='size-4' />
                        </button>

                        <div className='aspect-video overflow-hidden rounded-3xl bg-black'>
                            {source.kind === 'embed' ? (
                                <iframe
                                    src={source.src}
                                    title={label}
                                    allow='autoplay; fullscreen; picture-in-picture; encrypted-media'
                                    allowFullScreen
                                    className='size-full'
                                />
                            ) : (
                                <video src={source.src} controls autoPlay playsInline className='size-full' />
                            )}
                        </div>
                    </div>
                )}
            </dialog>
        </>
    )
}
